/* eslint-disable @typescript-eslint/no-explicit-any */
import JanusError from './misc/error'
import JanusMessage from './misc/message'
import Session from './session'
import Transaction from './tx/transaction'
import TransactionManager from './tx/transaction-manager'
import Websocket from './websocket'

export interface ConnectionOptions {
  token?: string
  apisecret?: string
  keepalive: boolean | number
  pc?: RTCConfiguration
}

interface ConnectionEvents {
  close: () => void
  open: () => void
  error: (error: Error) => void
  message: (message: JanusMessage) => void
}

class Connection extends TransactionManager<ConnectionEvents> {
  private readonly address: string
  private readonly sessions: {}
  private readonly options: ConnectionOptions
  private readonly websocketConnection: Websocket

  constructor(address: string, options: ConnectionOptions) {
    super()

    this.address = address
    this.sessions = {}
    this.options = options

    this.websocketConnection = new Websocket()
    this.initWebsocketListeners()
  }

  getAddress(): string {
    return this.address
  }

  getOptions(): ConnectionOptions {
    return this.options
  }

  async open(): Promise<Connection> {
    await this.websocketConnection.open(this.address, 'janus-protocol')
    return this
  }

  async close(): Promise<void> {
    if (this.websocketConnection.isOpened()) {
      for (const session of this.getSessionList()) {
        await session.cleanup()
      }

      this.emit('close')
    }
  }

  isClosed(): boolean {
    return this.websocketConnection.isClosed()
  }

  createSession(): Promise<Session> {
    return this.sendSync({ janus: 'create' }, this)
  }

  hasSession(sessionId: string): boolean {
    return !!this.getSession(sessionId)
  }

  getSession(sessionId: string): Session {
    return this.sessions[sessionId]
  }

  getSessionList(): Session[] {
    return Object.keys(this.sessions).map((id) => this.sessions[id])
  }

  addSession(session: Session) {
    this.sessions[session.getId()] = session
    session.once('destroy', () => this.removeSession(session.getId()))
  }

  removeSession(sessionId: string) {
    delete this.sessions[sessionId]
  }

  send(message: any): Promise<any> {
    if (this.options.token) {
      message.token = this.options.token
    }

    if (this.options.apisecret) {
      message.apisecret = this.options.apisecret
    }

    if (!message.transaction) {
      message.transaction = Transaction.generateRandomId()
    }

    return this.websocketConnection.send(message)
  }

  processOutcomeMessage(message: any): Promise<any> {
    if (message.janus === 'create') {
      return this.onCreate(message)
    }

    const sessionId = message['session_id']
    if (sessionId) {
      if (this.hasSession(sessionId)) {
        return this.getSession(sessionId).processOutcomeMessage(message)
      } else {
        return Promise.reject(new Error(`Invalid session: [${sessionId}]`))
      }
    }

    return Promise.resolve(message)
  }

  async processIncomeMessage(msg: JanusMessage): Promise<any> {
    this.emit('message', msg)
    const sessionId = msg.get('session_id')

    if (sessionId && this.hasSession(sessionId)) {
      return this.getSession(sessionId).processIncomeMessage(msg)
    }

    if (sessionId && !this.hasSession(sessionId)) {
      throw new Error(`Invalid session: [${sessionId}]`)
    }

    return await this.defaultProcessIncomeMessage(msg)
  }

  toString() {
    return `[Connection] ${JSON.stringify({ address: this.address })}`
  }

  private onCreate(outMsg: any): Promise<JanusMessage> {
    this.addTransaction(
      new Transaction(outMsg.transaction, (msg: JanusMessage) => {
        if (msg.get('janus') === 'success') {
          const sessionId = msg.get('data', 'id')
          this.addSession(new Session(this, sessionId))
          return this.getSession(sessionId)
        } else {
          throw new JanusError(msg)
        }
      }),
    )

    return Promise.resolve(outMsg)
  }

  private initWebsocketListeners() {
    this.websocketConnection.on('open', () => this.emit('open'))
    this.websocketConnection.on('error', (error) => this.emit('error', error))
    this.websocketConnection.on('close', () => this.emit('close'))
    this.websocketConnection.on('message', (msg) => {
      this.processIncomeMessage(new JanusMessage(msg)).catch((error) => this.emit('error', error))
    })
  }
}

export default Connection
